import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Donut score gauge — 0-100 scale, counts up on mount over ~600ms.
 *
 * Phase 12 calibration (matches the new 8-category rubric):
 *   <50      = below average → destructive (red)
 *   50–64    = warning amber
 *   65–78    = primary (the brand amber)
 *   79–88    = success green
 *   89+      = success-emphasis (deeper green with a subtle ring/glow)
 */
export const ScoreGauge = ({ score, size = 192, strokeWidth = 14 }) => {
    const [displayed, setDisplayed] = useState(0);
    const clamped = Math.max(0, Math.min(100, score ?? 0));

    useEffect(() => {
        const duration = 600;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplayed(Math.round(eased * clamped));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [clamped]);

    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - displayed / 100);

    // Phase 12 colour tiers — five bands matching the rubric calibration.
    const tone =
        clamped >= 89 ? "stroke-status-success"
        : clamped >= 79 ? "stroke-status-success"
        : clamped >= 65 ? "stroke-primary"
        : clamped >= 50 ? "stroke-status-warning"
        : "stroke-destructive";

    const calibrationLabel =
        clamped >= 89 ? "Exceptional"
        : clamped >= 79 ? "Genuinely skilful"
        : clamped >= 65 ? "Solid"
        : clamped >= 50 ? "Typical first attempt"
        : "Below average";

    const isTopTier = clamped >= 89;

    return (
        <div className="relative inline-flex flex-col items-center" data-testid="score-gauge">
            <svg width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    strokeWidth={strokeWidth}
                    className="stroke-muted/60"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className={cn(
                        "transition-[stroke-dashoffset] duration-200 ease-out",
                        tone,
                        isTopTier && "drop-shadow-[0_0_8px_hsl(var(--status-success)/0.55)]"
                    )}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl font-semibold tabular-nums text-foreground" data-testid="score-gauge-value">
                    {displayed}
                </span>
                <span className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                    of 100
                </span>
            </div>
            <span
                className="mt-3 inline-flex rounded-full bg-secondary px-3 py-1 text-xs font-medium text-foreground"
                data-testid="score-gauge-tier"
            >
                {calibrationLabel}
            </span>
        </div>
    );
};
